import { useTranslation } from '@shared/core/i18n';
import { addError } from '@shared/utils/logger';
import type { GraphErrorTranslations } from '@locales/generated';

type ValidCode = keyof GraphErrorTranslations['code'];

const UNKNOWN_ERROR_CODE: ValidCode = 'UNKNOWN';
const SUPPORT_EMAIL = 'support@withjoy.com';

/**
 * Helper to resolve translation value which can be either a string or a function
 */
const resolveTranslation = (value: string | (() => string) | undefined): string | undefined => {
  if (typeof value === 'function') return value();
  return typeof value === 'string' ? value : undefined;
};

/**
 * Hook to translate error codes using the graphError namespace
 */
export const useTranslateGraphError = () => {
  const { t, tq } = useTranslation('graphError');
  const genericErrorMessage = tq`An unknown error occurred.`;

  /**
   * Translate error code to i18n message
   * Returns undefined if the code is UNKNOWN or translation not found
   *
   * @see doc/plans/kyc-failure-messaging.md for implementation details
   */
  const translateGraphErrorCode = (code: string | undefined): string | undefined => {
    if (code === undefined) return undefined;
    if (code === UNKNOWN_ERROR_CODE) return undefined;

    try {
      const translated = resolveTranslation(t('code', <ValidCode>code));
      if (translated === undefined || translated === code) return undefined;

      const contactUs = t('shared', 'contactUs');
      const customerSupport = typeof contactUs === 'function' ? contactUs({ email: SUPPORT_EMAIL }) : undefined;
      return customerSupport ? `${translated} ${customerSupport}` : translated;
    } catch (err) {
      addError(err, { context: 'useTranslateGraphError.translateGraphErrorCode', code });
      return undefined;
    }
  };

  /**
   * Translate error code with fallback for UNKNOWN or missing translations
   * Always returns a message (uses fallback for UNKNOWN/missing)
   *
   * @see doc/plans/kyc-failure-messaging.md for implementation details
   */
  const translateGraphErrorCodeOrUnknown = (code: string | undefined, fallback?: string): string => {
    const unknownMessage = (): string => {
      if (typeof fallback === 'string') return fallback;

      try {
        return resolveTranslation(t('code', UNKNOWN_ERROR_CODE)) ?? genericErrorMessage;
      } catch (err) {
        addError(err, { context: 'useTranslateGraphError.translateGraphErrorCodeOrUnknown', code });
        return genericErrorMessage;
      }
    };

    if (code === undefined || code === UNKNOWN_ERROR_CODE) {
      return unknownMessage();
    }
    return translateGraphErrorCode(code) ?? unknownMessage();
  };

  return {
    translateGraphErrorCode,
    translateGraphErrorCodeOrUnknown,
  };
};
